import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SearchInput from '../components/SearchInput';
import FeatureCarousel from '../components/FeatureCarousel';
import LiveIntelligence from '../components/LiveIntelligence';
import WorkflowSection from '../components/WorkflowSection';
import FAQ from '../components/FAQ';
import '../styles/HomePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const PLACEHOLDER_EXAMPLES = [
  "Recent graduates building in agent payments",
  "Space-tech founders with several years of industry experience",
  "Highly-technical founders building in biotech",
  "Former FAANG engineers building AI infrastructure tools",
  "PhDs in machine learning building enterprise solutions",
  "Climate-tech founders with experience at Y Combinator companies",
  "Serial entrepreneurs building in fintech",
  "Stanford dropouts working on robotics automation",
  "Healthcare professionals building digital health platforms",
  "Ex-consultants creating B2B SaaS products",
  "MIT graduates building quantum computing applications",
  "First-time founders with 10+ years in cybersecurity",
  "Technical co-founders from Ivy League schools building edtech",
  "Ex-SpaceX engineers working on aerospace startups",
  "Design-focused founders building consumer social apps"
];

function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showDashboardButton, setShowDashboardButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);

  // Query expansion state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [formattedQuery, setFormattedQuery] = useState(null);

  const handleGoClick = async (event) => {
    event.preventDefault();
    if (!searchQuery.trim()) {
      setErrorMessage('Please enter a search query.');
      return;
    }
    setErrorMessage('');

    // If query is already expanded, show the additional fields
    if (isExpanded) {
      setShowAdditionalFields(true);
      return;
    }

    // Otherwise, expand the query first
    const startTime = Date.now();
    const minDuration = 2500; // 2.5 seconds minimum
    setIsExpanding(true);

    try {
      const response = await fetch(`${BACKEND_URL}/query/expand-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: searchQuery.trim() })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to expand query');
      }

      const parsed = await response.json();

      // Wait for minimum duration before applying changes
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDuration - elapsedTime);

      await new Promise(resolve => setTimeout(resolve, remainingTime));

      // Now apply all the state updates together
      setSearchQuery(parsed.expanded_unformatted_query);
      setFormattedQuery(parsed.expanded_formatted_query);
      setIsExpanded(true);
      setIsHighlighting(true);
      setIsExpanding(false);
      setTimeout(() => setIsHighlighting(false), 3500);
    } catch (err) {
      console.error('Error expanding query:', err);
      setErrorMessage(err.message || 'Failed to expand your query. Please try again.');
      setIsExpanding(false);
    }
  };

  const handleEditQuery = () => {
    setShowAdditionalFields(false);
    setErrorMessage('');
    // Clear the form fields when editing
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedCompanyName = companyName.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (!trimmedFirstName || !trimmedLastName || !trimmedCompanyName || !trimmedEmail || !trimmedPassword || !trimmedConfirmPassword) {
      setErrorMessage('Please fill in all fields.');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (trimmedPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (!supabase) {
      setErrorMessage('Service is temporarily unavailable. Please try again soon.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Check if user with this email already exists in Auth
      const serverUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const response = await fetch(`${serverUrl}/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (data.exists) {
        setPopupMessage("An account with that email already exists. Please login or contact us at hello@getcappuccino.com if this isn't you.");
        setShowPopup(true);
        setIsSubmitting(false);
        return;
      }

      // Create account and submit search
      const signupResponse = await fetch(`${serverUrl}/auth/sign-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: trimmedEmail,
          password: trimmedPassword,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
          company_name: trimmedCompanyName,
          query: searchQuery.trim(),
        }),
      });

      const signupData = await signupResponse.json();

      if (!signupResponse.ok) {
        setErrorMessage(signupData.error || 'Something went wrong. Please try again.');
        return;
      }

      // Auto-login: Set the session using the tokens returned from signup
      if (signupData.access_token && signupData.refresh_token) {
        await supabase.auth.setSession({
          access_token: signupData.access_token,
          refresh_token: signupData.refresh_token,
        });

        // Create the search job with the user's query
        await fetch(`${serverUrl}/leads/create-find-leads-job`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${signupData.access_token}`,
          },
          body: JSON.stringify({ query: searchQuery.trim() }),
        });

        // Navigate to dashboard
        navigate('/dashboard');
        return;
      }

      // Fallback if no tokens (e.g., email confirmation required)
      setSearchQuery('');
      setFirstName('');
      setLastName('');
      setCompanyName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setShowAdditionalFields(false);

      setPopupMessage("Account created! Please check your email to confirm your account, then log in.");
      setShowDashboardButton(false);
      setShowPopup(true);
    } catch (err) {
      console.error('Submission failed:', err);
      setErrorMessage('Unable to connect right now. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1 className="app-title">
          Cappuccino
        </h1>
        <Link to="/login" className="app-login-button">
          Login
        </Link>
      </header>

      <main className="app-main">
        <div className="app-hero">
          <h2 className="app-hero-title">
            Find founders before they're <span className="app-hero-title-accent">found.</span>
          </h2>
          <p className="app-hero-subtitle">
            The new standard for sourcing. We track signals across the entire internet to surface high-potential founders before they even incorporate.
          </p>

          {!showSearchInput && (
            <div className="app-hero-buttons">
              <button
                className="app-try-free-button"
                onClick={() => setShowSearchInput(true)}
              >
                Try a Demo
              </button>
              <a href="https://www.loom.com/share/400577fed4924d669fe00e7c91c7f035" target="_blank" rel="noopener noreferrer" className="app-view-demo-link">
                ▶ View Demo
              </a>
            </div>
          )}
        </div>

        {!showSearchInput && <FeatureCarousel />}

        {showSearchInput && (
          <section className="app-search-section">
            <form className="app-search-form" onSubmit={handleGoClick}>
              <SearchInput
                query={searchQuery}
                onQueryChange={setSearchQuery}
                onSubmit={handleGoClick}
                isExpanded={isExpanded}
                isExpanding={isExpanding}
                isSubmitting={isSubmitting}
                isHighlighting={isHighlighting}
                formattedQuery={formattedQuery}
                onHighlightingStop={() => setIsHighlighting(false)}
                disabled={showAdditionalFields}
                placeholderExamples={PLACEHOLDER_EXAMPLES}
                expandedButtonText="Start search"
                hintTextDefault={<>Hint: Specify <strong>location</strong>, <strong>funding stage</strong>, <strong>founder qualities</strong>, and <strong>product industry</strong></>}
                showEditButton={showAdditionalFields}
                onEditClick={handleEditQuery}
              />
            </form>

            {showAdditionalFields && (
              <form className="app-additional-fields" onSubmit={handleSubmit}>
                <p className="app-create-account-text">Create an account to view your curated search results once they are ready.</p>
                <div className="app-fields-grid">
                  <input
                    type="text"
                    className="app-field-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="text"
                    className="app-field-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="text"
                    className="app-field-input"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company Name"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="email"
                    className="app-field-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="password"
                    className="app-field-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="password"
                    className="app-field-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="app-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            )}

            {errorMessage && (
              <p className="app-error-message" role="alert">
                {errorMessage}
              </p>
            )}
          </section>
        )}

        <LiveIntelligence />

        <WorkflowSection />

        <FAQ />
      </main>

      {showPopup && (
        <div className="app-popup" role="dialog" aria-live="assertive">
          <div className="app-popup-card">
            <button
              type="button"
              className="app-popup-close-x"
              onClick={handleClosePopup}
              aria-label="Close popup"
            >
              &times;
            </button>
            {showDashboardButton && (
              <h3 className="app-popup-title">Account created!</h3>
            )}
            <p className="app-popup-message">{popupMessage}</p>
            {showDashboardButton ? (
              <button
                type="button"
                className="app-popup-dashboard-button"
                onClick={() => navigate('/dashboard')}
              >
                View dashboard
              </button>
            ) : (
              <button
                type="button"
                className="app-popup-dismiss"
                onClick={handleClosePopup}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      <footer className="app-footer">
        <a href="mailto:hello@getcappuccino.com" className="app-footer-link">hello@getcappuccino.com</a>
      </footer>
    </div>
  );
}

export default HomePage;
